/**
 * Yang dibaca layar LRLC dari muatannya, dan cara ia membacanya.
 *
 * Warna dan nama kelas datang dari server dan tidak ditulis ulang di sini:
 * keduanya dipakai lembar log, kolom kelas, tabel ringkasan dan plot silang,
 * dan empat salinan yang disamakan dengan tangan adalah empat salinan yang
 * akan berselisih. Yang tinggal di berkas ini hanyalah cara mencarinya.
 */
import type { LrlcClass, LrlcClassRow, ProductionTest } from "../types";

/**
 * Ketujuh kelas, cerminan `petro/lrlc.CLASSES` di server.
 *
 * Layar LRLC sendiri tidak membacanya — ia memakai daftar yang datang bersama
 * muatannya, sebab di situ server-lah yang berwenang. Yang membaca daftar ini
 * hanyalah **kolom kelas di lembar log**, yang menggambar kurva `LRLC_CLASS`
 * yang sudah diterbitkan dan karena itu tidak punya muatan LRLC untuk ditanya.
 * Meminta lembar log mengambil katalog LRLC berarti satu permintaan tambahan
 * pada tiap pembukaan sumur, demi tujuh warna yang tidak pernah berubah.
 *
 * Sebuah cerminan yang menyimpang tidak akan melempar; ia akan menggambar
 * ungu di tempat yang server sebut merah muda, dan tidak ada yang melihatnya.
 * Jadi kedua sisi dipatok uji pada nilai yang sama persis —
 * `lib/lrlc.test.ts` di sini dan `test_lrlc.py` di sana — dan yang menggeser
 * salah satunya akan melihat dua uji gagal, bukan nol.
 */
export const LRLC_CLASSES: LrlcClass[] = [
  { code: 0, key: "non_reservoir", label: "Non reservoir", color: "#d9d9d9" },
  { code: 1, key: "water", label: "Water", color: "#00bfff" },
  { code: 2, key: "lrlc_potential", label: "LRLC — potential", color: "#ffb6c1" },
  { code: 3, key: "lrlc_proven", label: "LRLC — proven", color: "#a020f0" },
  { code: 4, key: "lc_reservoir", label: "LC reservoir", color: "#ffa600" },
  { code: 5, key: "non_lc_reservoir", label: "Non-LC reservoir", color: "#8b1a1a" },
  { code: 6, key: "coal", label: "Coal", color: "#000000" },
];

/**
 * Pita kelas yang bersambung, dari kurva `LRLC_CLASS` pada sumbu kedalamannya.
 *
 * Sampel tanpa kelas memutus pitanya dan tidak ditelan tetangganya: lubang
 * data yang dicat sewarna lapisan di atasnya adalah lubang yang lenyap dari
 * lembarnya, dan yang membacanya tidak punya cara mengetahui ia pernah ada.
 */
export function classBlocks(
  depths: number[],
  values: (number | null)[],
): { top: number; base: number; code: number; fill: string; label: string }[] {
  const out: { top: number; base: number; code: number; fill: string; label: string }[] = [];
  let start = -1;
  let current: number | null = null;

  const close = (endIndex: number) => {
    if (start < 0 || current === null) return;
    const spec = LRLC_CLASSES.find((c) => c.code === current);
    if (spec) {
      out.push({
        top: depths[start],
        base: depths[Math.min(endIndex, depths.length - 1)],
        code: current, fill: spec.color, label: spec.label,
      });
    }
    start = -1;
    current = null;
  };

  for (let i = 0; i < depths.length; i += 1) {
    const code = classAt(values[i]);
    if (code === null) { close(i - 1); continue; }
    if (code !== current) { close(i - 1); start = i; current = code; }
  }
  close(depths.length - 1);
  return out.filter((block) => block.base > block.top);
}

/** Warna kelas menurut nomornya; yang tak dikenal memulangkan putih. */
export function colorOf(classes: LrlcClass[], code: number): string {
  return classes.find((c) => c.code === code)?.color ?? "#ffffff";
}

export function labelOf(classes: LrlcClass[], code: number): string {
  return classes.find((c) => c.code === code)?.label ?? `Class ${code}`;
}

/**
 * Kelas yang benar-benar ada di sumur ini, untuk legenda.
 *
 * Legenda yang memajang ketujuh kelas pada sumur yang hanya punya tiga membuat
 * yang membacanya mencari empat warna yang tidak ada di gambarnya.
 */
export function occupied(rows: LrlcClassRow[]): LrlcClassRow[] {
  return rows.filter((row) => row.n > 0);
}

/**
 * Ketebalan kelas LRLC — kedua kelas, dijumlahkan.
 *
 * Angka inilah yang dicari orang begitu layarnya terbuka: berapa meter yang
 * mungkin terlewat. Keduanya dijumlahkan sebab keduanya sama-sama terlewat —
 * yang satu belum diuji, yang lain sudah dan tetap tidak masuk hitungan
 * cadangan selama lognya membacanya sebagai air.
 */
export function lrlcThickness(rows: LrlcClassRow[]): number {
  return rows
    .filter((row) => row.code === 2 || row.code === 3)
    .reduce((sum, row) => sum + row.thickness, 0);
}

/**
 * Kalimat yang menerangkan mengapa tidak ada satu pun kelas "proven".
 *
 * `null` ketika memang ada, atau ketika tidak ada LRLC sama sekali — pada
 * keduanya tidak ada yang perlu diterangkan. Yang perlu diterangkan hanyalah
 * keadaan di tengah: ada lapisan yang berperilaku seperti LRLC, dan tidak satu
 * pun uji produksi yang bisa menaikkannya. Itu bukan kekurangan hitungan
 * melainkan kekurangan data, dan keduanya terbaca sama di layar.
 */
export function provenHint(rows: LrlcClassRow[], tests: number): string | null {
  const potential = rows.find((row) => row.code === 2)?.n ?? 0;
  const proven = rows.find((row) => row.code === 3)?.n ?? 0;
  if (proven > 0 || potential === 0) return null;
  return tests === 0
    ? "No production test on these wells, so nothing can be proven — every "
      + "low-resistivity pay stays potential until a test says otherwise."
    : "The production tests on these wells do not reach the flagged intervals.";
}

/** Selang uji yang menutupi sebuah kedalaman, kalau ada. */
export function testAt(tests: ProductionTest[], md: number): ProductionTest | null {
  return tests.find((t) => md >= t.top_md && md <= t.base_md) ?? null;
}

/**
 * Nilai kelas pada sebuah kedalaman, dari kurva `LRLC_CLASS` yang diterbitkan.
 *
 * Dibulatkan, sebab kurva disimpan sebagai bilangan pecahan dan interpolasi ke
 * sumbu kedalaman lain melahirkan 2,5 di perbatasan dua kelas. Yang setengah
 * jalan antara "potential" dan "proven" tidak ada; yang ada hanyalah tepi.
 */
export function classAt(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return Math.round(value);
}
